import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Send, Paperclip, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getMessages, sendMessage, uploadImage, getImageUrl } from '@/utils/api';
import { USER_ROLE_LABELS } from '@/utils/constants';

const ChatPanel = ({ taskId, taskName }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (taskId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [taskId, searchQuery]);

  useEffect(() => {
    if (!searchQuery) {
      scrollToBottom();
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const response = await getMessages(taskId, searchQuery || null);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadImage(file);
      const imagePath = response.data.image;
      setAttachments([...attachments, imagePath]);
      toast.success('File attached');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    setLoading(true);
    try {
      await sendMessage(taskId, newMessage, attachments);
      setNewMessage('');
      setAttachments([]);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'master_admin':
        return 'bg-rose-100 text-rose-700';
      case 'admin':
        return 'bg-purple-100 text-purple-700';
      case 'cad_head':
        return 'bg-teal-100 text-teal-700';
      case 'design_head':
        return 'bg-cyan-100 text-cyan-700';
      case 'designer':
        return 'bg-blue-100 text-blue-700';
      case 'customer':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-border" data-testid="chat-panel">
      {/* Header - Navy themed */}
      <div className="p-3 border-b border-border" style={{ backgroundColor: 'hsl(220, 60%, 20%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white text-sm">Discussion</h3>
            <p className="text-xs text-white/60 truncate">{taskName}</p>
          </div>
          <button
            onClick={() => setShowSearch(!showSearch)}
            className={`p-1.5 rounded-md transition-colors text-white ${showSearch ? 'bg-white/20' : 'hover:bg-white/10'}`}
            title="Search messages"
          >
            <Search size={14} />
          </button>
        </div>
        
        {showSearch && (
          <div className="mt-2">
            <Input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/50"
              data-testid="chat-search-input"
            />
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="messages-container">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <p className="text-sm">{searchQuery ? 'No messages match your search' : 'No messages yet'}</p>
            {!searchQuery && <p className="text-xs mt-1">Start the conversation</p>}
          </div>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.sender_id === user?.id;
            return (
              <div
                key={msg.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.id}`}
              >
                <div className={`max-w-[85%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                  {!isOwn && (
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs font-medium text-slate-700">{msg.sender_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${getRoleBadgeColor(msg.sender_role)}`}>
                        {USER_ROLE_LABELS[msg.sender_role] || msg.sender_role}
                      </span>
                    </div>
                  )}
                  <div
                    className={`rounded-lg px-3 py-2 ${
                      isOwn ? 'text-white' : 'bg-muted text-foreground'
                    }`}
                    style={isOwn ? { backgroundColor: 'hsl(220, 60%, 20%)' } : {}}
                  >
                    <p className="text-xs whitespace-pre-wrap">{msg.message}</p>
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {msg.attachments.map((att, idx) => {
                          const imgUrl = getImageUrl(att);
                          return (
                            <img
                              key={idx}
                              src={imgUrl}
                              alt="Attachment"
                              className="max-w-full rounded cursor-pointer max-h-40"
                              onClick={() => window.open(imgUrl, '_blank')}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-400 mt-0.5">
                    {new Date(msg.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 border-t border-slate-200 bg-white">
        {attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            {attachments.map((att, idx) => (
              <div key={idx} className="relative">
                <img src={getImageUrl(att)} alt="Preview" className="w-12 h-12 rounded object-cover" />
                <button
                  onClick={() => removeAttachment(idx)}
                  className="absolute -top-1 -right-1 p-0.5 bg-rose-500 text-white rounded-full hover:bg-rose-600"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-1.5">
          <label
            htmlFor="chat-file-upload"
            className="p-2 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer transition-colors"
          >
            <Paperclip size={16} className="text-slate-600" />
          </label>
          <input
            id="chat-file-upload"
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            disabled={uploading}
            className="hidden"
          />
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={loading}
            className="flex-1 h-9 text-xs"
            data-testid="message-input"
          />
          <Button
            onClick={handleSend}
            disabled={loading || (!newMessage.trim() && attachments.length === 0)}
            className="h-9"
            style={{ backgroundColor: 'hsl(220, 60%, 20%)' }}
            data-testid="send-message-button"
          >
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
